import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api, {
  fetchPersonnelPanel as apiFetchPersonnelPanel,
  fetchAssignableUsersByUnit as apiFetchAssignableUsersByUnit,
} from '../../services/belediyeApi';

/**
 * --- 1. ATAMA (Assignment) İŞLEMLERİ ---
 */

// Atama oluştur (başvuruya yeni assignment)
// Backend'e uygun, assigned_to yerine participants ile birlikte kullanılacaksa ekleme yapılır
export const createAssignment = createAsyncThunk(
  'assignment/createAssignment',
  async ({ application, assigned_to, note, token }, { rejectWithValue }) => {
    try {
      // Eğer bir assignment üzerinde birden fazla personel atanacaksa,
      // öncelikle assignment oluşturulur, ardından participants endpointine ekleme yapılır.
      const response = await api.post(
        `/staff/assignments/`,
        { application, assigned_to, note },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      // Detaylı hata mesajı yazdır
      
      return rejectWithValue(error.response?.data?.detail || 'Atama oluşturulamadı.');
    }
  }
);

export const fetchAssignmentsByApplication = createAsyncThunk(
  'assignment/fetchAssignmentsByApplication',
  async ({ applicationId, token }, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/staff/assignments/?application=${applicationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      //   DEFANSİF FİLTRE: Backend yanlışlıkla tüm assignment'ları döndürse bile
      // sadece bu application'a ait olanları store'a al.
      const raw = Array.isArray(response.data) ? response.data : [];
      const filtered = raw.filter((a) => {
        const appId =
          a?.application_detail?.id ?? a?.application ?? a?.application_id;
        return String(appId) === String(applicationId);
      });

      return filtered;
    } catch (error) {
      
      return rejectWithValue('Başvuru atamaları alınamadı.');
    }
  }
);

export const fetchPersonnelPanelData = createAsyncThunk(
  'assignment/fetchPersonnelPanelData',
  async ({ applicationId, history = false }, { rejectWithValue }) => {
    try {
      const data = await apiFetchPersonnelPanel(applicationId, { history });
      return data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.detail || 'Personel panel verileri alınamadı.'
      );
    }
  }
);

export const fetchAssignablePersonnelByUnit = createAsyncThunk(
  'assignment/fetchAssignablePersonnelByUnit',
  async ({ unitId }, { rejectWithValue }) => {
    try {
      const data = await apiFetchAssignableUsersByUnit(unitId);

      const list = Array.isArray(data) ? data : [];

      return {
  unitId,
  list: list.filter(
    (user) =>
      user?.is_assignable === true &&
      user?.personnel_step_visible !== false &&
      user?.user_is_active !== false &&
      (
        user?.membership_is_active === null ||
        user?.membership_is_active === undefined ||
        user?.membership_is_active === true
      )
  ),
};
    } catch (error) {
      return rejectWithValue({
        unitId,
        detail:
          error?.response?.data?.detail ||
          'Atanabilir personel listesi alınamadı.',
      });
    }
  }
);

// Atama sil
export const deleteAssignment = createAsyncThunk(
  'assignment/deleteAssignment',
  async ({ assignmentId, token }, { rejectWithValue }) => {
    try {
      await api.delete(`/staff/assignments/${assignmentId}/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return assignmentId;
    } catch (error) {
      
      return rejectWithValue('Atama silinemedi.');
    }
  }
);

/**
 * --- 2. KATILIMCI (Participant) İŞLEMLERİ ---
 */

// Katılımcı (personel) ekle
export const addParticipant = createAsyncThunk(
  'assignment/addParticipant',
  async ({ assignment, user, is_responder, token }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/staff/participants/`,
        { assignment, user, is_responder },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      
      // API'nin döndürdüğü error.detail varsa onu al
      return rejectWithValue(error.response?.data?.detail || 'Katılımcı eklenemedi.');
    }
  }
);

// Katılımcı listesini getir (bir atama için)
export const fetchParticipants = createAsyncThunk(
  'assignment/fetchParticipants',
  async ({ assignmentId, token }, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/staff/participants/?assignment=${assignmentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      
      return rejectWithValue('Katılımcılar alınamadı.');
    }
  }
);

export const fetchParticipantsForAssignment = createAsyncThunk(
  'assignment/fetchParticipantsForAssignment',
  async ({ assignmentId, token }, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/staff/participants/?assignment=${assignmentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return { assignmentId, list: response.data };
    } catch (error) {
      
      return rejectWithValue({
        assignmentId,
        detail: 'Katılımcılar alınamadı.'
      });
    }
  }
);


// Katılımcı sil
export const deleteParticipant = createAsyncThunk(
  'assignment/deleteParticipant',
  async ({ participantId, assignmentId, token }, { rejectWithValue }) => {
    try {
      await api.delete(`/staff/participants/${participantId}/`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // reducer'a assignmentId de dön (cache temizliği için)
      return { participantId, assignmentId };
    } catch (error) {
      
      return rejectWithValue('Katılımcı silinemedi.');
    }
  }
);


// Katılımcı güncelle (ör: responder yetkisi değiştir)
export const updateParticipant = createAsyncThunk(
  'assignment/updateParticipant',
  async ({ participantId, is_responder, token }, { rejectWithValue }) => {
    try {
      const response = await api.patch(
        `/staff/participants/${participantId}/`,
        { is_responder },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      
      return rejectWithValue(error.response?.data?.detail || 'Katılımcı güncellenemedi.');
    }
  }
);

/**
 * --- 3. DEFAULT RESPONDER (Varsayılan Yanıtlayıcı) ---
 */

// Birim için varsayılan responder getir
export const fetchDefaultResponder = createAsyncThunk(
  'assignment/fetchDefaultResponder',
  async ({ unitId, token }, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/staff/default-responders/?unit=${unitId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Birime birden fazla default responder varsa ilkini alıyoruz
      return response.data.length > 0 ? response.data[0] : null;
    } catch (error) {
      
      return rejectWithValue('Varsayılan responder alınamadı.');
    }
  }
);

// Birim için varsayılan responder ata/güncelle
export const setDefaultResponder = createAsyncThunk(
  'assignment/setDefaultResponder',
  async ({ unit, responder, token }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/staff/default-responders/`,
        { unit, responder },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      
      return rejectWithValue(error.response?.data?.detail || 'Varsayılan responder atanamadı.');
    }
  }
);


// İlk responder'ı otomatik olarak varsayılan yanıtlayıcı olarak kaydeden thunk
export const saveDefaultResponderIfNeeded = createAsyncThunk(
  'assignment/saveDefaultResponderIfNeeded',
  async ({ unit, responder, token }, { getState, dispatch, rejectWithValue }) => {
    // Eğer zaten default responder atanmışsa tekrar atama!
    try {
      const state = getState().assignment;
      if (state.defaultResponder && state.defaultResponder.responder === responder) {
        // Zaten atanmış, tekrar atama
        return state.defaultResponder;
      }
      // Backend'de POST ile default responder ata
      const response = await api.post(
        `/staff/default-responders/`,
        { unit, responder },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Başarılı
      return response.data;
    } catch (error) {
      
      return rejectWithValue(error.response?.data?.detail || 'Varsayılan yanıtlayıcı atanamadı.');
    }
  }
);

// 1. FEEDBACK ACTIONLARI ----------------------------

// Katılımcıya (participant) ait tüm feedbackleri getir
export const fetchFeedbacksByParticipant = createAsyncThunk(
  'assignment/fetchFeedbacksByParticipant',
  async ({ participantId, token }, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/staff/task-feedbacks/?participant=${participantId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return { participantId, feedbacks: response.data };
    } catch (error) {
      
      return rejectWithValue('Geri bildirimler alınamadı.');
    }
  }
);

// Katılımcı için feedback ekle (JSON, dosya yoksa)
// Not: Dosya upload gerekirse FormData ile ayrıca yapılmalı!
export const addFeedback = createAsyncThunk(
  'assignment/addFeedback',
  async ({ participant, status, note, token }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/staff/task-feedbacks/`,
        { participant, status, note },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      
      return rejectWithValue(error.response?.data?.detail || 'Geri bildirim eklenemedi.');
    }
  }
);





// --- [EKLEME SONU] ---


const assignmentSlice = createSlice({
  name: 'assignment',
  initialState: {
    assignments: [],
    participants: [],
    defaultResponder: null,
    feedbacks: {},           // participantId: [feedback, ...] şeklinde dictionary
    feedbackLoading: false,
    feedbackError: null,
    loading: false,
    error: null,
    participantLoading: false,
    participantError: null,
    deleteLoading: false,
    deleteError: null,
    responderLoading: false,
    responderError: null,
    currentApplicationId: null,   // hangi başvuru için çalışıyoruz
    currentAssignmentId: null,    // hangi assignment’ın participants’ını en son istedik
    participantsByAssignmentId: {}, // { [assignmentId]: Participant[] }
    assignmentsById: {}, // { [assignmentId]: Assignment } (kolay erişim)
    activeAssignmentId: null, // UI şu an hangi assignment'ı gösteriyor (eski currentAssignmentId yerine)
    
participantsRequestIdByAssignmentId: {}, // { [assignmentId]: requestId }
participantsLoadingByAssignmentId: {},   // { [assignmentId]: boolean }
participantsErrorByAssignmentId: {},     // { [assignmentId]: string|null }
assignmentsRequestId: null,        // fetchAssignmentsByApplication için son request
assignmentsRequestedAppId: null,   // hangi application için istendi

    personnelPanel: null,
    personnelPanelLoading: false,
    personnelPanelError: null,

    assignableUsers: [],
    assignableUsersLoading: false,
    assignableUsersError: null,
assignableUsersRequestId: null,
assignableUsersUnitId: null,


  },
  reducers: {
    clearAssignmentState: (state) => {
      state.assignments = [];
      state.participants = [];
      state.defaultResponder = null;
      state.feedbacks = {};
      state.feedbackLoading = false;
      state.feedbackError = null;
      state.loading = false;
      state.error = null;
      state.participantLoading = false;
      state.participantError = null;
      state.deleteLoading = false;
      state.deleteError = null;
      state.responderLoading = false;
      state.responderError = null;
      state.currentApplicationId = null;
      state.currentAssignmentId = null;
      state.participantsByAssignmentId = {};
      state.assignmentsById = {};
      state.participantsLoadingByAssignmentId = {};
state.participantsErrorByAssignmentId = {};
state.assignmentsRequestId = null;
state.assignmentsRequestedAppId = null;

      state.personnelPanel = null;
      state.personnelPanelLoading = false;
      state.personnelPanelError = null;

      state.assignableUsers = [];
      state.assignableUsersLoading = false;
      state.assignableUsersError = null;
state.assignableUsersRequestId = null;
state.assignableUsersUnitId = null;

    },
   setActiveAssignmentId: (state, action) => {
  const assignmentId = action.payload ?? null;

  state.activeAssignmentId = assignmentId;
  state.currentAssignmentId = assignmentId; //   geriye dönük uyumluluk için senkron

  if (
    assignmentId != null &&
    Array.isArray(state.participantsByAssignmentId?.[assignmentId])
  ) {
    state.participants = state.participantsByAssignmentId[assignmentId];
  } else {
    state.participants = [];
  }
},



/* --- EKLEME BAŞLANGIÇ --- */
    // Başvuru değişince ilgili state'leri otomatik resetle
    resetAssignmentStateForNewApplication: (state, action) => {
  const applicationId = action.payload ?? null;

  state.currentApplicationId = applicationId;
  state.currentAssignmentId = null;

  state.participants = [];
  state.assignments = [];
  state.defaultResponder = null;

  state.feedbacks = {};
  state.feedbackLoading = false;
  state.feedbackError = null;

  state.loading = false;
  state.error = null;

  state.participantLoading = false;
  state.participantError = null;

  state.deleteLoading = false;
  state.deleteError = null;

  state.responderLoading = false;
  state.responderError = null;

  state.participantsByAssignmentId = {};
state.assignmentsById = {};
state.participantsLoadingByAssignmentId = {};
state.participantsErrorByAssignmentId = {};
state.assignmentsRequestId = null;
state.assignmentsRequestedAppId = null;

  state.personnelPanel = null;
  state.personnelPanelLoading = false;
  state.personnelPanelError = null;

  state.assignableUsers = [];
  state.assignableUsersLoading = false;
  state.assignableUsersError = null;
state.assignableUsersRequestId = null;
state.assignableUsersUnitId = null;
},

    /* --- EKLEME BİTİŞ --- */
  },


  
  extraReducers: (builder) => {
    builder
      // --- Assignment ---
      .addCase(createAssignment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAssignment.fulfilled, (state, action) => {
  state.loading = false;

  const created = action.payload;

  // assignments list + normalize
  if (created?.id != null) {
    state.assignments.push(created);
    state.assignmentsById[created.id] = created;

    // yeni oluşturulan assignment aktif olsun
    state.activeAssignmentId = created.id;
    state.currentAssignmentId = created.id; //   ekle
    // cache başlat
    if (!Array.isArray(state.participantsByAssignmentId[created.id])) {
      state.participantsByAssignmentId[created.id] = [];
    }

    // UI participants sıfır (bu yeni assignment'ın participant'ları henüz eklenecek)
    state.participants = [];
  }

  // hangi application için oluşturuldu (meta'dan)
  const requestedAppId = action.meta?.arg?.application ?? null;
  if (requestedAppId != null) state.currentApplicationId = requestedAppId;
})


      .addCase(createAssignment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })


      .addCase(fetchPersonnelPanelData.pending, (state) => {
        state.personnelPanelLoading = true;
        state.personnelPanelError = null;
      })
      .addCase(fetchPersonnelPanelData.fulfilled, (state, action) => {
        state.personnelPanelLoading = false;
        state.personnelPanelError = null;

        const payload = action.payload || {};
        const assignments = Array.isArray(payload.assignments) ? payload.assignments : [];
        const latestAssignmentId =
          payload.latest_assignment_id ??
          (assignments.length > 0 ? assignments[0].id : null);

        state.personnelPanel = payload;
        state.currentApplicationId = payload.application_id ?? state.currentApplicationId;

        state.assignments = assignments;
        state.assignmentsById = {};
        state.participantsByAssignmentId = {};

        for (const assignment of assignments) {
          if (assignment?.id == null) continue;

          state.assignmentsById[assignment.id] = assignment;
          state.participantsByAssignmentId[assignment.id] = Array.isArray(assignment.participants)
            ? assignment.participants
            : [];
        }

        state.activeAssignmentId = latestAssignmentId ?? null;
        state.currentAssignmentId = latestAssignmentId ?? null;

        if (
          latestAssignmentId != null &&
          Array.isArray(state.participantsByAssignmentId[latestAssignmentId])
        ) {
          state.participants = state.participantsByAssignmentId[latestAssignmentId];
        } else {
          state.participants = [];
        }
      })
      .addCase(fetchPersonnelPanelData.rejected, (state, action) => {
        state.personnelPanelLoading = false;
        state.personnelPanelError = action.payload || 'Personel panel verileri alınamadı.';
      })



     .addCase(fetchAssignablePersonnelByUnit.pending, (state, action) => {
  state.assignableUsersLoading = true;
  state.assignableUsersError = null;

  state.assignableUsersRequestId = action.meta?.requestId || null;
  state.assignableUsersUnitId = action.meta?.arg?.unitId ?? null;
})
.addCase(fetchAssignablePersonnelByUnit.fulfilled, (state, action) => {
  const reqId = action.meta?.requestId;
  const requestedUnitId = action.meta?.arg?.unitId ?? null;

  // stale response koruması
  if (
    state.assignableUsersRequestId &&
    state.assignableUsersRequestId !== reqId
  ) {
    return;
  }

  // unit mismatch koruması
  if (
    state.assignableUsersUnitId != null &&
    requestedUnitId != null &&
    String(state.assignableUsersUnitId) !== String(requestedUnitId)
  ) {
    return;
  }

  state.assignableUsersLoading = false;
  state.assignableUsersError = null;

  const list = Array.isArray(action.payload?.list)
    ? action.payload.list
    : [];

  state.assignableUsers = list;
})
.addCase(fetchAssignablePersonnelByUnit.rejected, (state, action) => {
  const reqId = action.meta?.requestId;

  if (
    state.assignableUsersRequestId &&
    state.assignableUsersRequestId !== reqId
  ) {
    return;
  }

  state.assignableUsersLoading = false;
  state.assignableUsersError =
    action.payload?.detail ||
    action.payload ||
    'Atanabilir personel listesi alınamadı.';
})



      .addCase(fetchAssignmentsByApplication.pending, (state, action) => {
  state.loading = true;
  state.error = null;

  const requestedAppId = action.meta?.arg?.applicationId ?? null;
  const reqId = action.meta?.requestId;

  //   son request’i kaydet (stale response engeli için)
  state.assignmentsRequestId = reqId;
  state.assignmentsRequestedAppId = requestedAppId;

  //   Başvuru değiştiyse: eski veriyi anında temizle (UI’da karışmasın)
  if (
    requestedAppId != null &&
    state.currentApplicationId != null &&
    String(requestedAppId) !== String(state.currentApplicationId)
  ) {
    state.currentApplicationId = requestedAppId;

    state.assignments = [];
    state.assignmentsById = {};

    state.activeAssignmentId = null;
    state.currentAssignmentId = null;

    state.participants = [];
    state.participantsByAssignmentId = {};

    state.participantsRequestIdByAssignmentId = {};
    state.participantsLoadingByAssignmentId = {};
    state.participantsErrorByAssignmentId = {};
  }

  // İlk load’da da currentApplicationId set et (ve ekranda eski kalıntıyı temiz tut)
  if (state.currentApplicationId == null && requestedAppId != null) {
    state.currentApplicationId = requestedAppId;

    state.assignments = [];
    state.assignmentsById = {};
    state.participants = [];
    state.participantsByAssignmentId = {};
    state.activeAssignmentId = null;
    state.currentAssignmentId = null;
  }
})

    
      .addCase(fetchAssignmentsByApplication.fulfilled, (state, action) => {
  state.loading = false;

   const reqId = action.meta?.requestId;
  const requestedAppId = action.meta?.arg?.applicationId ?? null;

//   stale response: son requestId değilse ignore
  if (state.assignmentsRequestId && state.assignmentsRequestId !== reqId) {
    return;
  }

  //   ekstra güvenlik: application mismatch ise ignore
  if (
    requestedAppId != null &&
    state.currentApplicationId != null &&
    String(requestedAppId) !== String(state.currentApplicationId)
  ) {
    return;
  }

  // İlk kez set
  if (state.currentApplicationId == null) {
    state.currentApplicationId = requestedAppId;
  }

  // Eğer response başka application için geldiyse: komple resetle
  if (
    requestedAppId != null &&
    state.currentApplicationId != null &&
    String(requestedAppId) !== String(state.currentApplicationId)
  ) {
    state.currentApplicationId = requestedAppId;

    state.assignments = [];
    state.assignmentsById = {};

    state.participants = [];
    state.participantsByAssignmentId = {};

    state.activeAssignmentId = null;

    state.participantsRequestIdByAssignmentId = {};
    state.participantsLoadingByAssignmentId = {};
    state.participantsErrorByAssignmentId = {};
  }

  const list = Array.isArray(action.payload) ? action.payload : [];
  state.assignments = list;

  // normalize
  state.assignmentsById = {};
  for (const a of list) {
    if (a?.id != null) state.assignmentsById[a.id] = a;
  }

  // "en son assignment" seçimi (id büyük olanı alıyoruz)
  let latestId = null;
  for (const a of list) {
    if (a?.id == null) continue;
    if (latestId == null || Number(a.id) > Number(latestId)) latestId = a.id;
  }

  // UI aktif assignment: daha önce seçilmemişse latest'e set et
  if (state.activeAssignmentId == null && latestId != null) {
    state.activeAssignmentId = latestId;
  }

  // Ekranda gösterilecek participants: aktif assignment cache'inden gelsin
  if (
    state.activeAssignmentId != null &&
    Array.isArray(state.participantsByAssignmentId?.[state.activeAssignmentId])
  ) {
    state.participants = state.participantsByAssignmentId[state.activeAssignmentId];
  } else {
    state.participants = [];
  }
})



      .addCase(fetchAssignmentsByApplication.rejected, (state, action) => {
  const reqId = action.meta?.requestId;
  if (state.assignmentsRequestId && state.assignmentsRequestId !== reqId) {
    return; // stale reject
  }
  state.loading = false;
  state.error = action.payload;
})


      .addCase(deleteAssignment.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
      })
      .addCase(deleteAssignment.fulfilled, (state, action) => {
        const deletedId = action.payload;

delete state.assignmentsById?.[deletedId];
delete state.participantsByAssignmentId?.[deletedId];
delete state.participantsLoadingByAssignmentId?.[deletedId];
delete state.participantsErrorByAssignmentId?.[deletedId];

if (state.currentAssignmentId != null && String(state.currentAssignmentId) === String(deletedId)) {
  state.currentAssignmentId = null;
  state.participants = [];
}

        state.deleteLoading = false;
        state.assignments = state.assignments.filter(a => a.id !== action.payload);
        // Silinen assignment'ın katılımcılarını da temizle
        state.participants = [];
      })
      .addCase(deleteAssignment.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload;
      })

      // --- Participant ---
      .addCase(addParticipant.pending, (state) => {
        state.participantLoading = true;
        state.participantError = null;
      })
      
      .addCase(addParticipant.fulfilled, (state, action) => {
  state.participantLoading = false;

  const p = action.payload;

  // current participants listesi
  const exists = state.participants.some((x) => x.id === p.id);
  if (!exists) state.participants.push(p);

  // cache (participantsByAssignmentId) güncelle
  const aId = String(p.assignment);
  const list = Array.isArray(state.participantsByAssignmentId?.[aId])
    ? state.participantsByAssignmentId[aId]
    : [];

  const existsInCache = list.some((x) => x.id === p.id);
  if (!existsInCache) {
    state.participantsByAssignmentId[aId] = [...list, p];
  }
})


      .addCase(addParticipant.rejected, (state, action) => {
        state.participantLoading = false;
        state.participantError = action.payload;
      })

      .addCase(fetchParticipants.pending, (state, action) => {
  state.participantError = null;
 

  const assignmentId = action.meta?.arg?.assignmentId ?? null;
  const reqId = action.meta?.requestId;

  if (assignmentId == null) {
    state.participantLoading = true; // fallback
    return;
  }

  state.participantsRequestIdByAssignmentId[assignmentId] = reqId;
  state.participantsLoadingByAssignmentId[assignmentId] = true;
  state.participantsErrorByAssignmentId[assignmentId] = null;
state.activeAssignmentId = assignmentId;
state.currentAssignmentId = assignmentId;

  // Genel loading'i de yak (istersen)
  state.participantLoading = true;
})

  .addCase(fetchParticipants.fulfilled, (state, action) => {
  const assignmentId = action.meta?.arg?.assignmentId ?? null;
  const reqId = action.meta?.requestId;

  const list = Array.isArray(action.payload) ? action.payload : [];

  if (assignmentId == null) {
    state.participantLoading = false;
    state.participants = list;
    return;
  }

  // stale response guard (assignment bazlı!)
  const lastReqId = state.participantsRequestIdByAssignmentId?.[assignmentId];
  if (lastReqId && lastReqId !== reqId) {
    return;
  }

  state.participantsLoadingByAssignmentId[assignmentId] = false;
  state.participantsErrorByAssignmentId[assignmentId] = null;

  // cache'e yaz
  state.participantsByAssignmentId[assignmentId] = list;

  // UI: eğer bu assignment aktifse ekrana bas
  if (state.activeAssignmentId != null && String(state.activeAssignmentId) === String(assignmentId)) {
    state.participants = list;
  }

  // global loading'i kapat
  state.participantLoading = false;
})
 
.addCase(fetchParticipantsForAssignment.pending, (state, action) => {
  const assignmentId = action.meta?.arg?.assignmentId;
  if (assignmentId == null) return;
  state.participantsLoadingByAssignmentId[assignmentId] = true;
  state.participantsErrorByAssignmentId[assignmentId] = null;
})
.addCase(fetchParticipantsForAssignment.fulfilled, (state, action) => {
  const { assignmentId, list } = action.payload || {};
  if (assignmentId == null) return;

  state.participantsLoadingByAssignmentId[assignmentId] = false;

  const arr = Array.isArray(list) ? list : [];
  state.participantsByAssignmentId[assignmentId] = arr;

  // Eğer bu assignment aktif olan ise ekrana basılan participants'ı da güncelle
  if (state.currentAssignmentId != null && String(state.currentAssignmentId) === String(assignmentId)) {
    state.participants = arr;
  }
})
.addCase(fetchParticipantsForAssignment.rejected, (state, action) => {
  const assignmentId = action.payload?.assignmentId ?? action.meta?.arg?.assignmentId;
  if (assignmentId == null) return;

  state.participantsLoadingByAssignmentId[assignmentId] = false;
  state.participantsErrorByAssignmentId[assignmentId] =
    action.payload?.detail || action.payload || 'Katılımcılar alınamadı.';
})


.addCase(fetchParticipants.rejected, (state, action) => {
  const assignmentId = action.meta?.arg?.assignmentId ?? null;
  const reqId = action.meta?.requestId;

  if (assignmentId == null) {
    state.participantLoading = false;
    state.participantError = action.payload;
    return;
  }

  const lastReqId = state.participantsRequestIdByAssignmentId?.[assignmentId];
  if (lastReqId && lastReqId !== reqId) {
    return;
  }

  state.participantsLoadingByAssignmentId[assignmentId] = false;
  state.participantsErrorByAssignmentId[assignmentId] = action.payload;

  state.participantLoading = false;
  state.participantError = action.payload;
})



      .addCase(deleteParticipant.pending, (state) => {
  state.participantLoading = true;
  state.participantError = null;
})
.addCase(deleteParticipant.fulfilled, (state, action) => {
  state.participantLoading = false;

  const { participantId, assignmentId } = action.payload || {};

  // assignmentId geldiyse: sadece o cache'i ve ekranı temizle
  if (assignmentId != null) {
    const cached = Array.isArray(state.participantsByAssignmentId[assignmentId])
      ? state.participantsByAssignmentId[assignmentId]
      : [];

    const nextCached = cached.filter((p) => String(p.id) !== String(participantId));
    state.participantsByAssignmentId[assignmentId] = nextCached;

    // aktif assignment ise ekrandaki liste de güncellensin
    if (state.currentAssignmentId != null && String(state.currentAssignmentId) === String(assignmentId)) {
      state.participants = nextCached;
    }

    return;
  }

  // fallback: assignmentId gelmezse ekrandan sil + tüm cache’lerden temizle
  state.participants = state.participants.filter((p) => String(p.id) !== String(participantId));
  for (const key of Object.keys(state.participantsByAssignmentId || {})) {
    const arr = state.participantsByAssignmentId[key];
    if (Array.isArray(arr)) {
      state.participantsByAssignmentId[key] = arr.filter((p) => String(p.id) !== String(participantId));
    }
  }
})
.addCase(deleteParticipant.rejected, (state, action) => {
  state.participantLoading = false;
  state.participantError = action.payload;
})


      .addCase(updateParticipant.pending, (state) => {
        state.participantLoading = true;
        state.participantError = null;
      })
     .addCase(updateParticipant.fulfilled, (state, action) => {
  state.participantLoading = false;

  const updated = action.payload;
  const assignmentId = updated?.assignment ?? state.currentAssignmentId;

  if (assignmentId != null) {
    const cached = Array.isArray(state.participantsByAssignmentId[assignmentId])
      ? state.participantsByAssignmentId[assignmentId]
      : [];

    const idx = cached.findIndex((x) => String(x.id) === String(updated.id));
    if (idx >= 0) {
      cached[idx] = updated;
    } else {
      cached.push(updated);
    }

    state.participantsByAssignmentId[assignmentId] = cached;

    if (state.currentAssignmentId != null && String(state.currentAssignmentId) === String(assignmentId)) {
      state.participants = cached;
    }
  } else {
    // fallback
    state.participants = state.participants.map((p) =>
      String(p.id) === String(updated.id) ? updated : p
    );
  }

  const responders = state.participants.filter((p) => p.is_responder);
  if (updated?.is_responder && responders.length === 1) {
    // istersen burada defaultResponder tetik mantığını korursun
  }
})

      .addCase(updateParticipant.rejected, (state, action) => {
        state.participantLoading = false;
        state.participantError = action.payload;
      })

      // --- Default Responder ---
      .addCase(fetchDefaultResponder.pending, (state) => {
        state.responderLoading = true;
        state.responderError = null;
      })
      .addCase(fetchDefaultResponder.fulfilled, (state, action) => {
        state.responderLoading = false;
        state.defaultResponder = action.payload;
      })
      .addCase(fetchDefaultResponder.rejected, (state, action) => {
        state.responderLoading = false;
        state.responderError = action.payload;
      })

      .addCase(setDefaultResponder.pending, (state) => {
        state.responderLoading = true;
        state.responderError = null;
      })
      .addCase(setDefaultResponder.fulfilled, (state, action) => {
        state.responderLoading = false;
        state.defaultResponder = action.payload;
      })
      .addCase(setDefaultResponder.rejected, (state, action) => {
        state.responderLoading = false;
        state.responderError = action.payload;
      })

      // 4. FEEDBACK REDUCER'LARI
      .addCase(fetchFeedbacksByParticipant.pending, (state) => {
        state.feedbackLoading = true;
        state.feedbackError = null;
      })
      .addCase(fetchFeedbacksByParticipant.fulfilled, (state, action) => {
        state.feedbackLoading = false;
        // participantId’ye göre kaydet
        const { participantId, feedbacks } = action.payload;
        state.feedbacks[participantId] = feedbacks;
      })
      .addCase(fetchFeedbacksByParticipant.rejected, (state, action) => {
        state.feedbackLoading = false;
        state.feedbackError = action.payload;
      })

      .addCase(addFeedback.pending, (state) => {
        state.feedbackLoading = true;
        state.feedbackError = null;
      })
      .addCase(addFeedback.fulfilled, (state, action) => {
        state.feedbackLoading = false;
        // Yeni feedback’i ilgili participant’ın feedbacks dizisine ekle
        const participant = action.payload.participant;
        if (state.feedbacks[participant]) {
          state.feedbacks[participant].unshift(action.payload); // Yeni başa ekle
        } else {
          state.feedbacks[participant] = [action.payload];
        }
      })
      .addCase(addFeedback.rejected, (state, action) => {
        state.feedbackLoading = false;
        state.feedbackError = action.payload;
      });


  },
});

export const {
  clearAssignmentState,
  resetAssignmentStateForNewApplication,
  setActiveAssignmentId, //   EKLE
} = assignmentSlice.actions;



export default assignmentSlice.reducer;
